import React, { useState, useEffect } from 'react';
import { useWallet } from '../../context/WalletContext';
import { useContractServices } from '../../context/ContractServicesContext';
import { toast } from 'react-toastify';
import { 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  Award, 
  RefreshCw, 
  Wallet, 
  Coins, 
  Shield, 
  ArrowRight,
  Zap,
  History,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import skyLoader from "../../assets/home-assets/skyLoader.gif";

const EmissionsTab = () => {
  const { walletAddress, isConnected } = useWallet();
  const { services, isReady } = useContractServices();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [epochInfo, setEpochInfo] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [claimingProject, setClaimingProject] = useState(null);
  const [claimingType, setClaimingType] = useState(null);
  const [error, setError] = useState(null);
  const [expandedEpochs, setExpandedEpochs] = useState({});
  
  useEffect(() => {
    if (isReady && isConnected) {
      // Use an async function to properly sequence the API calls
      const loadData = async () => {
        setLoading(true);
        try {
          // First get epoch info
          await fetchEpochInfo();
          // Then get user projects (which depends on epoch info)
          await fetchUserProjects();
        } catch (err) {
          console.error("Error loading initial data:", err);
          setError("Failed to load emissions data. Please try refreshing.");
        } finally {
          setLoading(false);
        }
      };
      
      loadData();
    }
  }, [isReady, isConnected]);
  
  const fetchEpochInfo = async () => {
    try {
      const info = await services.emissionController.getCurrentEpochInfo();
      setEpochInfo(info);
      return info; // Return the info so it can be used directly
    } catch (error) {
      console.error('Error fetching epoch info:', error);
      setError('Failed to load epoch information. Please try again.');
      throw error; // Propagate the error to be handled by the caller
    }
  };
  
  const fetchUserProjects = async () => {
    setError(null);
    try {
      // Get fresh epoch info or use the existing one
      let currentEpochInfo = epochInfo;
      if (!currentEpochInfo) {
        console.log('No epoch info available, fetching now...');
        try {
          currentEpochInfo = await fetchEpochInfo();
        } catch (error) {
          console.error('Failed to get epoch info, cannot check emissions');
          setUserProjects([]);
          return;
        }
      }

      // Make sure we have valid epoch info
      if (!currentEpochInfo) {
        console.error('Failed to get epoch info, cannot check emissions');
        setUserProjects([]);
        return;
      }

      // Get the total number of projects from the contract
      let totalProjects = 0;
      try {
        totalProjects = await services.innovationUnits.getTotalProjects();
        console.log('Total projects from contract:', totalProjects);
      } catch (error) {
        console.error('Error getting total projects:', error);
        // Use a fallback number if the call fails
        totalProjects = 20;
        console.log('Using fallback project count:', totalProjects);
      }

      // Check emissions for all epochs up to the current one
      const currentEpoch = currentEpochInfo.currentEpochNumber;
      const allEpochsData = {};
      
      // Start checking from epoch 1 (or a reasonable starting point) up to current epoch - 1
      for (let epochNumber = 1; epochNumber < currentEpoch; epochNumber++) {
        console.log(`Checking unclaimed emissions for epoch ${epochNumber}`);
        
        const epochProjects = [];
        
        for (let projectId = 1; projectId <= totalProjects; projectId++) {
          try {
            // Check if this project even exists
            let projectExists = true;
            try {
              if (services.innovationUnits.projectIdExists) {
                projectExists = await services.innovationUnits.projectIdExists(projectId);
              }
            } catch (error) {
              // If we can't check if project exists, assume it does
              console.log(`Could not check if project ${projectId} exists, assuming it does:`, error);
            }
            
            if (projectExists) {
              // Check for unclaimed staking emissions
              const stakingEmissions = await services.emissionController.checkUnclaimedStakingEmissions(
                epochNumber,
                projectId,
                walletAddress
              );
              
              // Check for unclaimed IU holder emissions
              const iuEmissions = await services.emissionController.checkUnclaimedIUHolderEmissions(
                epochNumber,
                projectId,
                walletAddress
              );
              
              if (stakingEmissions.hasUnclaimed || iuEmissions.hasUnclaimed) {
                console.log(`Found unclaimed emissions for project ${projectId} in epoch ${epochNumber}:`, { 
                  staking: stakingEmissions.hasUnclaimed, 
                  iuHolder: iuEmissions.hasUnclaimed 
                });
                
                // Try to get project details, but don't fail if it's not available
                let projectName = `Project #${projectId}`;
                try {
                  // First try to use the new getProjectName function
                  if (services.innovationUnits.getProjectName) {
                    try {
                      const name = await services.innovationUnits.getProjectName(projectId);
                      if (name) projectName = name;
                    } catch (e) {
                      console.log(`Error using getProjectName for project ${projectId}:`, e);
                    }
                  }
                  
                  // Fallback to getProjectData which might include name
                  if (projectName === `Project #${projectId}` && services.innovationUnits.getProjectData) {
                    try {
                      const projectData = await services.innovationUnits.getProjectData(projectId);
                      // Check for name in the returned data
                      if (projectData && projectData.name) projectName = projectData.name;
                    } catch (e) {
                      console.log(`Error getting project data for project ${projectId}:`, e);
                    }
                  }
                } catch (error) {
                  console.log(`Could not get details for project ${projectId}:`, error);
                }
                
                epochProjects.push({
                  projectId,
                  projectName,
                  stakingEmissions: stakingEmissions.amount,
                  iuHolderEmissions: iuEmissions.amount,
                  formattedStakingEmissions: stakingEmissions.formattedAmount,
                  formattedIuHolderEmissions: iuEmissions.formattedAmount,
                  hasStakingEmissions: stakingEmissions.hasUnclaimed,
                  hasIuHolderEmissions: iuEmissions.hasUnclaimed
                });
              }
            }
          } catch (error) {
            console.log(`Error checking project ${projectId} for epoch ${epochNumber}:`, error);
            // Continue with next project, don't abort the whole process
          }
        }
        
        // If there are any projects with unclaimed emissions for this epoch, add it to the result
        if (epochProjects.length > 0) {
          allEpochsData[epochNumber] = epochProjects;
          
          // Automatically expand the most recent epoch with unclaimed emissions
          if (Object.keys(expandedEpochs).length === 0) {
            setExpandedEpochs(prev => ({...prev, [epochNumber]: true}));
          }
        }
      }
      
      console.log('Final epochs with emissions:', allEpochsData);
      setUserProjects(allEpochsData);
    } catch (error) {
      console.error('Error fetching user projects:', error);
      toast.error('Failed to load emissions data');
    }
  };
  
  // Also update the refresh button click handler
  const handleRefresh = async () => {
    setLoading(true);
    try {
      // First update epoch info
      const newEpochInfo = await fetchEpochInfo();
      // Then update user projects with the new epoch info
      await fetchUserProjects();
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleClaimEmissions = async (projectId, type, epochNumber) => {
    setClaimingProject(projectId);
    setClaimingType(type);
    
    try {
      if (type === 'staking') {
        await services.emissionController.claimStakingEmissions(
          epochNumber, 
          projectId
        );
      } else if (type === 'iuHolder') {
        await services.emissionController.claimIUHolderEmissions(
          epochNumber,
          projectId
        );
      }
      
      // Refresh data
      fetchUserProjects();
    } catch (error) {
      console.error(`Error claiming ${type} emissions:`, error);
    } finally {
      setClaimingProject(null);
      setClaimingType(null);
    }
  };
  
  // Helper function to format epoch time remaining
  const formatEpochTimeRemaining = () => {
    if (!epochInfo || !epochInfo.isActive) return 'No active epoch';
    
    const now = Math.floor(Date.now() / 1000);
    const timeRemaining = epochInfo.endTime - now;
    
    if (timeRemaining <= 0) return 'Epoch ended';
    
    const days = Math.floor(timeRemaining / 86400);
    const hours = Math.floor((timeRemaining % 86400) / 3600);
    const minutes = Math.floor((timeRemaining % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };
  
  const toggleEpochExpansion = (epochNumber) => {
    setExpandedEpochs(prev => ({
      ...prev,
      [epochNumber]: !prev[epochNumber]
    }));
  };
  
  // Get total number of projects with emissions across all epochs
  const getTotalProjectsCount = () => {
    const allProjectIds = new Set();
    
    Object.values(userProjects).forEach(epochProjects => {
      epochProjects.forEach(project => {
        allProjectIds.add(project.projectId);
      });
    });
    
    return allProjectIds.size;
  };
  
  // Get total number of unclaimed emissions
  const getTotalUnclaimedCount = () => {
    let total = 0;
    
    Object.values(userProjects).forEach(epochProjects => {
      epochProjects.forEach(project => {
        if (project.hasStakingEmissions) total++;
        if (project.hasIuHolderEmissions) total++;
      });
    });
    
    return total;
  };
  
  if (!isConnected) {
    return (
      <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-sm text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
          <AlertCircle className="h-8 w-8 text-yellow-500" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-3">Wallet Not Connected</h3>
        <p className="text-gray-600 max-w-sm mx-auto mb-6">Connect your wallet to view and claim your CEL token emissions</p>
        <button 
          onClick={() => toast.info('Connect wallet functionality would be triggered here')}
          className="px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg hover:from-indigo-600 hover:to-indigo-700 transition-all shadow-sm flex items-center mx-auto"
        >
          <Wallet className="h-4 w-4 mr-2" />
          Connect Wallet
        </button>
      </div>
    );
  }
  
  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen ">
          <img 
            src={skyLoader} 
            alt="Loading..." 
            className="w-100 h-100 object-contain"
          />
        </div>
    );
  
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 text-white">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="flex items-center">
            <div className="bg-white/20 p-2 rounded-lg mr-4">
              <Zap className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">CEL Token Emissions</h2>
              <p className="text-indigo-100 mt-1">
                Claim your rewards from staking and holding Innovation Units
              </p>
            </div>
          </div>
          
          <button 
            onClick={handleRefresh} 
            disabled={loading}
            className="px-4 py-2.5 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 flex items-center justify-center md:self-end transition-all"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      <div className="p-6">
        {/* Display error if present */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <p className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              {error}
            </p>
            <p className="text-sm mt-2 ml-7">Try refreshing or connecting your wallet again if the issue persists.</p>
          </div>
        )}
        
        {/* Current Epoch Info */}
        <div className="mb-6 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl overflow-hidden shadow-sm">
          <div className="px-6 py-4 bg-indigo-100/50 border-b border-indigo-100">
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-indigo-600 mr-2" />
              <h3 className="text-lg font-semibold text-indigo-900">
                Epoch Information
              </h3>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-50">
                <p className="text-sm text-indigo-400 uppercase tracking-wider mb-2">Current Epoch</p>
                <p className="font-semibold text-gray-900 text-lg">{epochInfo?.currentEpochNumber || 'Unknown'}</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-50">
                <p className="text-sm text-indigo-400 uppercase tracking-wider mb-2">Status</p>
                {epochInfo?.isActive ? (
                  <div className="font-semibold text-green-600 flex items-center">
                    <div className="h-3 w-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                    <CheckCircle className="h-4 w-4 mr-1" /> Active
                  </div>
                ) : (
                  <div className="font-semibold text-gray-600 flex items-center">
                    <div className="h-3 w-3 bg-gray-400 rounded-full mr-2"></div>
                    Inactive
                  </div>
                )}
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-50">
                <p className="text-sm text-indigo-400 uppercase tracking-wider mb-2">Time Remaining</p>
                <div className="font-semibold text-gray-900 flex items-center">
                  <Clock className="h-4 w-4 text-indigo-500 mr-2" />
                  {formatEpochTimeRemaining()}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Available Emissions */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <div className="bg-indigo-100 p-1.5 rounded-lg mr-3">
              <Award className="h-5 w-5 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Unclaimed Emissions</h3>
            {Object.keys(userProjects).length > 0 && (
              <div className="ml-2 bg-indigo-100 text-indigo-700 text-xs font-medium px-2 py-0.5 rounded-full">
                {getTotalUnclaimedCount()} rewards across {Object.keys(userProjects).length} epoch{Object.keys(userProjects).length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
          
          {Object.keys(userProjects).length === 0 ? (
            <div className="text-center p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-4">
                <Award className="h-8 w-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-medium text-gray-700 mb-2">No Unclaimed Emissions</h4>
              <p className="text-gray-500 max-w-md mx-auto">
                You don't have any unclaimed emissions from past epochs. Emissions are distributed at the end of each epoch to stakers and IU holders.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(userProjects)
                .sort(([epochA], [epochB]) => parseInt(epochB) - parseInt(epochA)) // Sort by epoch number (descending)
                .map(([epochNumber, projects]) => (
                <div key={epochNumber} className="border border-indigo-100 rounded-xl overflow-hidden shadow-md">
                  <div 
                    onClick={() => toggleEpochExpansion(epochNumber)}
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 px-5 py-4 border-b border-indigo-200 cursor-pointer hover:from-indigo-600 hover:to-purple-600 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="bg-white/20 p-1.5 rounded-lg mr-3">
                          <History className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-white text-lg flex items-center">
                            Epoch {epochNumber}
                            <span className="ml-3 text-sm bg-white/20 px-2 py-0.5 rounded-full">
                              {projects.length} project{projects.length !== 1 ? 's' : ''}
                            </span>
                          </h4>
                        </div>
                      </div>
                      <div className="flex items-center text-white">
                        {expandedEpochs[epochNumber] ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {expandedEpochs[epochNumber] && (
                    <div className="p-5 bg-white">
                      {projects.map(project => (
                        <div key={project.projectId} className="mb-6 last:mb-0 border border-indigo-100 rounded-xl overflow-hidden shadow-sm">
                          <div 
                            onClick={() => navigate(`/projects/token/${project.projectId}`)}
                            className="flex items-center justify-between bg-gradient-to-r from-indigo-100 to-indigo-50 px-5 py-4 cursor-pointer hover:from-indigo-200 hover:to-indigo-100 transition-colors border-b border-indigo-100"
                          >
                            <div className="flex items-center">
                              <div className="bg-indigo-500 p-2 rounded-lg mr-3">
                                <Coins className="h-5 w-5 text-white" />
                              </div>
                              <div>
                                <h5 className="font-bold text-gray-900 text-lg flex items-center">
                                  {project.projectName || `Project #${project.projectId}`}
                                  <ArrowRight className="h-4 w-4 ml-1.5 text-indigo-500" />
                                </h5>
                                <p className="text-xs text-indigo-700 mt-0.5">
                                  Project ID: {project.projectId}
                                </p>
                              </div>
                            </div>
                            <div className="bg-indigo-500 text-white text-xs px-3 py-1.5 rounded-full font-medium flex items-center">
                              <div className="h-2 w-2 bg-white rounded-full mr-1.5 animate-pulse"></div>
                              {(project.hasStakingEmissions && project.hasIuHolderEmissions) ? '2 Rewards' : '1 Reward'} Available
                            </div>
                          </div>
                          
                          <div className="p-5 bg-white">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              {project.hasStakingEmissions && (
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100 relative overflow-hidden">
                                  <div className="absolute top-0 right-0 w-16 h-16 bg-green-200 rounded-full -mr-6 -mt-6 opacity-30"></div>
                                  <h5 className="text-sm text-green-600 uppercase tracking-wider mb-2 font-semibold">Staking Emissions</h5>
                                  <div className="flex justify-between items-end">
                                    <p className="text-xl font-bold text-gray-800">
                                      {typeof project.formattedStakingEmissions === 'string' ? parseFloat(project.formattedStakingEmissions).toFixed(2) : project.formattedStakingEmissions} <span className="text-green-600">CEL</span>
                                    </p>
                                    <button
                                      onClick={() => handleClaimEmissions(project.projectId, 'staking', epochNumber)}
                                      disabled={claimingProject === project.projectId}
                                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-sm transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {claimingProject === project.projectId && claimingType === 'staking' ? (
                                        <>
                                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                                          Claiming...
                                        </>
                                      ) : (
                                        <>
                                          Claim Rewards <ArrowRight className="ml-2 h-4 w-4" />
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              )}
                              
                              {project.hasIuHolderEmissions && (
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100 relative overflow-hidden">
                                  <div className="absolute top-0 right-0 w-16 h-16 bg-blue-200 rounded-full -mr-6 -mt-6 opacity-30"></div>
                                  <h5 className="text-sm text-blue-600 uppercase tracking-wider mb-2 font-semibold">IU Holder Emissions</h5>
                                  <div className="flex justify-between items-end">
                                    <p className="text-xl font-bold text-gray-800">
                                      {typeof project.formattedIuHolderEmissions === 'string' ? parseFloat(project.formattedIuHolderEmissions).toFixed(2) : project.formattedIuHolderEmissions} <span className="text-blue-600">CEL</span>
                                    </p>
                                    <button
                                      onClick={() => handleClaimEmissions(project.projectId, 'iuHolder', epochNumber)}
                                      disabled={claimingProject === project.projectId}
                                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {claimingProject === project.projectId && claimingType === 'iuHolder' ? (
                                        <>
                                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                                          Claiming...
                                        </>
                                      ) : (
                                        <>
                                          Claim Rewards <ArrowRight className="ml-2 h-4 w-4" />
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Info Section */}
        <div className="mt-10 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 rounded-xl p-6 border border-indigo-100 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="bg-indigo-100 p-1.5 rounded-lg mr-3">
              <Shield className="h-5 w-5 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-indigo-900">About Emissions</h3>
          </div>
          
          <div className="bg-white rounded-lg p-5 shadow-sm">
            <p className="text-gray-700 mb-3">
              CEL tokens are distributed at the end of each epoch to reward:
            </p>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-start">
                <div className="bg-green-100 p-1 rounded-md mr-3 mt-0.5">
                  <Coins className="h-4 w-4 text-green-600" />
                </div>
                <p className="text-gray-700">
                  <span className="font-medium text-green-700">Project stakers</span> who provide liquidity and support projects
                </p>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-100 p-1 rounded-md mr-3 mt-0.5">
                  <Shield className="h-4 w-4 text-blue-600" />
                </div>
                <p className="text-gray-700">
                  <span className="font-medium text-blue-700">Innovation Unit holders</span> who have invested in projects
                </p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
              Emissions are calculated based on each project's impact score and your relative contribution.
              Unclaimed emissions from past epochs remain available for you to claim at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmissionsTab; 